import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const NAVY  = [11, 30, 45];
const TEAL  = [5, 196, 176];
const WHITE = [255, 255, 255];
const LIGHT_ROW = [247, 250, 252];
const LINE_CLR  = [220, 228, 235];

// A4 dimensions in mm
const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 14;
const CONTENT_W = PAGE_W - MARGIN * 2;

async function fetchAsDataUrl(url) {
  if (!url) return null;
  if (url.startsWith('data:')) return url;
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    return await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload  = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function fmtDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function imgFormat(dataUrl) {
  if (dataUrl.startsWith('data:image/png')) return 'PNG';
  if (dataUrl.startsWith('data:image/webp')) return 'WEBP';
  return 'JPEG';
}

// Returns natural aspect ratio (h/w) of a loaded image data URL
function getAspectRatio(dataUrl) {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => resolve(img.naturalHeight / img.naturalWidth);
    img.onerror = () => resolve(1.4); // fallback A4-ish ratio
    img.src = dataUrl;
  });
}

export async function generateExpensesPdf(expenses, {
  reportName    = 'Expense Report',
  ownerName     = null,
  dateRange     = null,
  defaultCurrency = 'USD',
  includeImages = false,
  onProgress    = null, // (done, total) => void
} = {}) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // ── Header band ──────────────────────────────────────────────────────────────
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, PAGE_W, 36, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...TEAL);
  doc.text('MySpend.info', MARGIN, 9);

  doc.setFontSize(15);
  doc.setTextColor(...WHITE);
  doc.text(reportName, MARGIN, 19);

  const total = expenses.reduce((s, e) => s + (parseFloat(e.convertedTotal ?? e.total) || 0), 0);
  const totalStr = `${defaultCurrency} ${total.toFixed(2)}`;

  const metaParts = [
    ownerName  ? `Submitted by: ${ownerName}` : null,
    dateRange  || null,
    `Total: ${totalStr}`,
  ].filter(Boolean);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(170, 195, 210);
  doc.text(metaParts.join('   ·   '), MARGIN, 27);

  doc.setFontSize(6.5);
  doc.setTextColor(110, 140, 160);
  doc.text(
    `Generated ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`,
    PAGE_W - MARGIN, 27, { align: 'right' }
  );

  // ── Expense table ─────────────────────────────────────────────────────────────
  const tableBody = expenses.map(e => {
    const convertedAmt = parseFloat(e.convertedTotal ?? e.total) || 0;
    const origAmt      = parseFloat(e.total) || 0;
    const amountCell   = `${defaultCurrency} ${convertedAmt.toFixed(2)}`
      + (e.currency && e.currency !== defaultCurrency
        ? `\n(${e.currency} ${origAmt.toFixed(2)})`
        : '');

    const noteParts   = [e.location, e.notes].filter(Boolean).join('  ·  ');
    const mealLine    = [e.mealType, e.guestName, e.guestAffiliation].filter(Boolean).join('  ·  ');
    const mileageLine = e.category === 'Mileage' && e.miles
      ? `${parseFloat(e.miles).toFixed(1)} mi @ $${parseFloat(e.mileageRate || 0).toFixed(3)}/mi`
        + ([e.mileageOrigin, e.mileageDest].filter(Boolean).length
          ? '  ·  ' + [e.mileageOrigin, e.mileageDest].filter(Boolean).join(' → ')
          : '')
        + (e.mileagePurpose ? '\n' + e.mileagePurpose : '')
      : null;
    const noteCell  = [mileageLine, mealLine, noteParts].filter(Boolean).join('\n');

    return [fmtDate(e.date), e.vendor || '', e.category || '', amountCell, noteCell];
  });

  autoTable(doc, {
    startY: 42,
    head: [['Date', 'Vendor', 'Category', 'Amount', 'Notes / Location']],
    body: tableBody,
    margin: { left: MARGIN, right: MARGIN },
    styles: {
      font: 'helvetica',
      fontSize: 8,
      cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
      lineColor: LINE_CLR,
      lineWidth: 0.15,
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: NAVY,
      textColor: WHITE,
      fontStyle: 'bold',
      fontSize: 8,
    },
    alternateRowStyles: { fillColor: LIGHT_ROW },
    columnStyles: {
      0: { cellWidth: 22 },
      1: { cellWidth: 48 },
      2: { cellWidth: 32 },
      3: { cellWidth: 30, halign: 'right' },
      4: { cellWidth: CONTENT_W - 22 - 48 - 32 - 30 },
    },
  });

  // ── Category summary ──────────────────────────────────────────────────────────
  const catMap = new Map();
  for (const e of expenses) {
    const cat = e.category || 'Uncategorized';
    const amt = parseFloat(e.convertedTotal ?? e.total) || 0;
    if (!catMap.has(cat)) catMap.set(cat, { count: 0, total: 0 });
    const entry = catMap.get(cat);
    entry.count++;
    entry.total += amt;
  }
  const catRows = [...catMap.entries()]
    .sort((a, b) => b[1].total - a[1].total)
    .map(([cat, { count, total: t }]) => [cat, count, `${defaultCurrency} ${t.toFixed(2)}`]);

  let cursorY = doc.lastAutoTable.finalY + 10;
  if (cursorY > PAGE_H - 55) { doc.addPage(); cursorY = 16; }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...NAVY);
  doc.text('Summary by Category', MARGIN, cursorY);

  autoTable(doc, {
    startY: cursorY + 4,
    head: [['Category', 'Expenses', 'Total']],
    body: catRows,
    foot: [[`${expenses.length} expense${expenses.length !== 1 ? 's' : ''}`, '', `${defaultCurrency} ${total.toFixed(2)}`]],
    margin: { left: MARGIN, right: MARGIN },
    tableWidth: 120,
    styles: {
      font: 'helvetica', fontSize: 8,
      cellPadding: { top: 3, right: 4, bottom: 3, left: 4 },
      lineColor: LINE_CLR, lineWidth: 0.15,
    },
    headStyles: { fillColor: NAVY, textColor: WHITE, fontStyle: 'bold', fontSize: 7.5 },
    alternateRowStyles: { fillColor: LIGHT_ROW },
    footStyles: { fillColor: NAVY, textColor: WHITE, fontStyle: 'bold', fontSize: 7.5 },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 22, halign: 'center' },
      2: { cellWidth: 35, halign: 'right' },
    },
  });

  // ── Receipt images — 2×2 grid ────────────────────────────────────────────────
  if (includeImages) {
    const withImages = expenses.filter(e => e.imagePreview);
    if (withImages.length > 0) {
      // Grid constants
      const COL_GAP    = 6;   // mm between columns
      const ROW_GAP    = 8;   // mm between rows
      const CAPTION_H  = 10;  // mm above each image for vendor + detail text
      const CELL_W     = (CONTENT_W - COL_GAP) / 2;  // 88mm
      const CELL_H     = 110; // mm — fixed image box height
      const GRID_TOP   = 22;  // mm from top of page (below the 16mm header band)

      // x origin for each column
      const colX = [MARGIN, MARGIN + CELL_W + COL_GAP];
      // y origin for each row (caption top)
      const rowY = [GRID_TOP, GRID_TOP + CAPTION_H + CELL_H + ROW_GAP];

      for (let i = 0; i < withImages.length; i++) {
        if (onProgress) onProgress(i, withImages.length);

        const posOnPage = i % 4;

        // New page at the start of every group of 4
        if (posOnPage === 0) {
          doc.addPage();
          doc.setFillColor(...NAVY);
          doc.rect(0, 0, PAGE_W, 16, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10);
          doc.setTextColor(...WHITE);
          doc.text('Receipt Images', MARGIN, 11);
        }

        const e = withImages[i];
        const dataUrl = await fetchAsDataUrl(e.imagePreview);
        if (!dataUrl) continue;

        const col = posOnPage % 2;
        const row = Math.floor(posOnPage / 2);
        const x   = colX[col];
        const y   = rowY[row];

        // ── Caption (2 lines) ──
        const vendor = e.vendor
          ? (e.vendor.length > 22 ? e.vendor.slice(0, 21) + '…' : e.vendor)
          : 'Unknown';
        const detail = [
          fmtDate(e.date),
          e.convertedTotal != null
            ? `${defaultCurrency} ${parseFloat(e.convertedTotal).toFixed(2)}`
            : (e.total != null ? `${e.currency || defaultCurrency} ${parseFloat(e.total).toFixed(2)}` : null),
        ].filter(Boolean).join('  ·  ');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        doc.setTextColor(...NAVY);
        doc.text(vendor, x, y + 4);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(100, 120, 140);
        doc.text(detail, x, y + 8.5);

        // ── Image box ──
        const boxX = x;
        const boxY = y + CAPTION_H;

        // Fill background, then draw border
        doc.setFillColor(245, 247, 250);
        doc.rect(boxX, boxY, CELL_W, CELL_H, 'F');
        doc.setDrawColor(...LINE_CLR);
        doc.setLineWidth(0.3);
        doc.rect(boxX, boxY, CELL_W, CELL_H);

        // Scale image to fit inside CELL_W × CELL_H, maintaining aspect ratio
        const ratio = await getAspectRatio(dataUrl);
        let renderW, renderH;
        if (ratio > CELL_H / CELL_W) {
          renderH = CELL_H;
          renderW = CELL_H / ratio;
        } else {
          renderW = CELL_W;
          renderH = CELL_W * ratio;
        }
        const offsetX = (CELL_W  - renderW) / 2;
        const offsetY = (CELL_H - renderH) / 2;

        doc.addImage(dataUrl, imgFormat(dataUrl), boxX + offsetX, boxY + offsetY, renderW, renderH);
      }

      if (onProgress) onProgress(withImages.length, withImages.length);
    }
  }

  // ── Page numbers ──────────────────────────────────────────────────────────────
  const numPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= numPages; p++) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(150, 160, 170);
    doc.text(`Page ${p} of ${numPages}`, PAGE_W - MARGIN, PAGE_H - 5, { align: 'right' });
  }

  doc.save(`${reportName.replace(/[^a-z0-9 _-]/gi, '_')}.pdf`);
}
